import React, { isValidElement, useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import useGet from "../../../hooks/common/useGet";
import { Table } from "../../common/Table";
import {
  beautify,
  downloadAsyncReport,
  downloadThroughAnchorLink,
  numberFormat,
  returnObject,
} from "../../../utils/helpers";
import GenericTabView from "../../layouts/GenericTabView";
import { Link } from "react-router-dom";
import { Breadcrumb, DatePicker, Select, Tooltip } from "antd";
import { ExportButton, LinkText } from "../../common/Buttons";
import { BASE_API_URL } from "../../../config";
import {
  API_ASYNC_REPORT,
  API_CASH_FLOW_EXPORT,
  API_FINANCIAL_POSITION_EXPORT,
  API_TRIAL_BALANCE,
  API_TRIAL_BALANCE_EXPORT,
  ASYNC_REPORT_PROFIT_AND_LOSS,
} from "../../../utils/apis";
import toast from "react-hot-toast";
import { buildTrialBalance, TTrialBalanceAccount } from "./trialBalanceUtils";

interface ReportItem {
  account_name: string;
  amount: number;
  general_ledger_code?: string;
  account_code?: string;
  id?: string;
}

type TReport =
  | "cash_flow"
  | "financial_position"
  | "profit_and_loss"
  | "trial_balance";
interface FormattedReportItem {
  account_code: React.ReactNode;
  account_name?: React.ReactNode;
  amount?: React.ReactNode;
  children?: FormattedReportItem[];
  id?: string;
}

interface TotalConfig {
  keys: string[];
  name: string;
  after?: string;
  subtract?: boolean;
}
interface ProcessReportDataOptions {
  orderedKeys?: string[];
  parentLevel?: number;
  totals?: TotalConfig[];
  collapsedKeys?: string[];
  expandedKeys?: Set<string>;
}

const getCurrentFinancialYear = () => {
  const today = dayjs();
  const currentYear = today.year();
  const currentMonth = today.month(); // 0-indexed (0 = January, 6 = July)

  // If current month is before July (month < 6), FY starts from July 1 of previous year
  // Otherwise, FY starts from July 1 of current year
  const startYear = currentMonth < 6 ? currentYear - 1 : currentYear;

  return {
    start_date: `${startYear}-07-01`,
    end_date: today.format("YYYY-MM-DD"),
  };
};

const BalanceSheetReports = () => {
  const {
    loadData: getFinancialPosition,
    data: financialPositionData,
    loading: financialPositionLoading,
  } = useGet();
  const {
    loadData: getCashFlow,
    data: cashFlowData,
    loading: cashFlowLoading,
  } = useGet();
  const {
    loadData: getIncomeSection,
    data: incomeData,
    loading: incomeLoading,
  } = useGet();
  const {
    loadData: getCostOfGoodsSold,
    data: costOfGoodsSoldData,
    loading: costOfGoodsSoldLoading,
  } = useGet();
  const {
    loadData: getOperatingExpenses,
    data: operatingExpensesData,
    loading: operatingExpensesLoading,
  } = useGet();
  const {
    loadData: getRevaluationLoss,
    data: revaluationLossData,
    loading: revaluationLossLoading,
  } = useGet();
  const {
    loadData: getTrialBalance,
    data: trialBalanceData,
    loading: trialBalanceLoading,
  } = useGet();
  const currentFY = getCurrentFinancialYear();
  const [filters, setFilters] = useState<{
    start_date?: string | null;
    end_date?: string | null;
    accounting_type?: string;
  }>({
    start_date: currentFY.start_date,
    end_date: currentFY.end_date,
    // end_date: new Date().toISOString().split("T")[0],
    accounting_type: "accrual",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const { RangePicker } = DatePicker;
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(
    new Set(),
  );

  const profitLossData =
    incomeData ||
    costOfGoodsSoldData ||
    operatingExpensesData ||
    revaluationLossData
      ? {
          ...(incomeData || {}),
          ...(costOfGoodsSoldData || {}),
          ...(operatingExpensesData || {}),
          ...(revaluationLossData || {}),
        }
      : null;

  const profitLossLoading =
    incomeLoading ||
    costOfGoodsSoldLoading ||
    operatingExpensesLoading ||
    revaluationLossLoading;

  useEffect(() => {
    getFinancialPosition({
      api: "reports/financial-position/",
      params: {
        end_date: filters.end_date,
        accounting_type: filters.accounting_type,
      },
    });
    getCashFlow({ api: "reports/cash-flow-statement/", params: filters });
    getIncomeSection({
      api: "reports/profit-and-loss-statement/",
      params: { ...filters, section: "income" },
    });
    getCostOfGoodsSold({
      api: "reports/profit-and-loss-statement/",
      params: { ...filters, section: "cost_of_goods_sold" },
    });
    getOperatingExpenses({
      api: "reports/profit-and-loss-statement/",
      params: { ...filters, section: "operating_expenses" },
    });
    getRevaluationLoss({
      api: "reports/profit-and-loss-statement/",
      params: { ...filters, section: "revaluation_loss" },
    });
    getTrialBalance({
      api: API_TRIAL_BALANCE,
      params: {
        end_date: filters.end_date,
        accounting_type: filters.accounting_type,
        page_size: 1000,
      },
    });
  }, [filters]);

  const handleDownloadAllProfitAndLoss = async () => {
    setIsDownloadingAll(true);
    try {
      await downloadAsyncReport(
        `${BASE_API_URL}${API_ASYNC_REPORT}`,
        "profit_and_loss.xlsx",
        { report: ASYNC_REPORT_PROFIT_AND_LOSS },
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to export profit and loss report");
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const handleDownload = async (report: TReport) => {
    if (report === "profit_and_loss") {
      setIsDownloading(true);
      try {
        await downloadAsyncReport(
          `${BASE_API_URL}${API_ASYNC_REPORT}`,
          "profit_and_loss_summary.xlsx",
          { report: ASYNC_REPORT_PROFIT_AND_LOSS, ...filters },
        );
      } catch (err: any) {
        toast.error(err?.message || "Failed to export profit and loss report");
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    let api,
      fileName = "";
    switch (report) {
      case "cash_flow":
        api = `${BASE_API_URL}${API_CASH_FLOW_EXPORT}`;
        fileName = "cash_flow_statement.xlsx";
        break;
      case "financial_position":
        api = `${BASE_API_URL}${API_FINANCIAL_POSITION_EXPORT}`;
        fileName = "financial_position.xlsx";
        break;
      case "trial_balance":
        api = `${BASE_API_URL}${API_TRIAL_BALANCE_EXPORT}`;
        fileName = "trial_balance.xlsx";
        break;
    }
    const params =
      report === "trial_balance"
        ? {
            end_date: filters.end_date,
            accounting_type: filters.accounting_type,
          }
        : filters;
    setIsDownloading(true);
    downloadThroughAnchorLink(api, fileName, params).finally(() => {
      setIsDownloading(false);
    });
  };

  const financialPositionTableColumns = () => [
    { id: "account_code", header: "Account Code" },
    { id: "account_name", header: "Account Name" },
    { id: "amount", header: "Amount", filter: true },
  ];

  const buildTransactionLink = (itemId: string | undefined) => {
    if (!itemId) return "#";
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    const queryString = params.toString();
    return `/transactions/${itemId}${queryString ? `?${queryString}` : ""}`;
  };

  const transformData = (key: string, items: any[]) => {
    let totalAmount = 0;
    const childData = items.map((item: any) => {
      totalAmount += item?.amount;
      return {
        account_code: (
          <AccountCode>
            {item?.general_ledger_code || item?.account_code}
          </AccountCode>
        ),
        account_name: (
          <LinkText
            to={buildTransactionLink(item?.id)}
            style={{
              justifyContent: "start",
            }}
          >
            {item.account_name}
          </LinkText>
        ),
        amount: (
          <LinkText to={buildTransactionLink(item?.id)}>
            {numberFormat(item?.amount)}
          </LinkText>
        ),
      };
    });
    return [
      {
        account_code: <b>{beautify(key)}</b>,
      },
      ...childData,
      ...returnObject(totalAmount !== 0, [
        {
          account_code: <b>{beautify(`Total ${key}`)}</b>,
          amount: <b>{numberFormat(totalAmount)}</b>,
        },
      ]),
    ];
  };

  const getTableData = (data: any) => {
    if (!data) return [];
    const dataKeys = Object.keys(data);
    return [...dataKeys.flatMap((key) => [...transformData(key, data[key])])];
  };

  const onToggleExpand = (key: string) => {
    const newKeys = new Set(expandedKeys);
    if (newKeys.has(key)) {
      newKeys.delete(key);
    } else {
      newKeys.add(key);
    }
    setExpandedKeys(newKeys);
  };

  const formatReportItem = ({
    item,
    boldKeys = [],
    collapsedKeys = [],
    expandedKeys = new Set(),
    level = 0,
  }: {
    item: ReportItem;
    boldKeys?: string[];
    collapsedKeys?: string[];
    expandedKeys?: Set<string>;
    level?: number;
  }): FormattedReportItem => {
    const checkBold = (accountName: string, value: React.ReactNode) =>
      boldKeys.includes(accountName) ? <b>{value}</b> : value;
    return {
      account_code: collapsedKeys?.includes(item.account_name) ? (
        <ClickableSpan onClick={() => onToggleExpand(item.account_name)}>
          <AccountCode>
            {checkBold(
              item.account_name,
              item.general_ledger_code || item.account_code,
            )}
            <span style={{ marginLeft: 5, fontSize: "0.8em" }}>
              {expandedKeys?.has(item?.account_name) ? "▼" : "▶"}
            </span>
          </AccountCode>
        </ClickableSpan>
      ) : (
        <AccountCode>
          {checkBold(
            item.account_name,
            item.general_ledger_code || item.account_code,
          )}
        </AccountCode>
      ),
      account_name: (
        <LinkText
          to={buildTransactionLink(item?.id)}
          style={{
            justifyContent: "start",
            marginLeft: `${(level - 1) * 20}px`,
          }}
        >
          {checkBold(item.account_name, item.account_name)}
        </LinkText>
      ),
      amount: (
        <LinkText to={buildTransactionLink(item?.id)}>
          {checkBold(item.account_name, numberFormat(item.amount))}
        </LinkText>
      ),
    };
  };

  const createTotalWithTooltip = (total: TotalConfig, value: number) => {
    const operation = total.subtract ? "-" : "+";
    const tooltipText = `${total.keys.join(` ${operation} `)}`;

    return {
      account_code: <b>{total.name || "Totals"}</b>,
      amount: (
        <Tooltip title={tooltipText}>
          <b>{numberFormat(value)}</b>,
        </Tooltip>
      ),
    };
  };

  const calculateTotals = (totals: any, totalsValues: any, data: any) => {
    if (!totals) return;

    totals.forEach((total) => {
      // Get the first key as base value
      const findAndSumAmount = (items: any[], searchKey: string): number => {
        return items.reduce((sum, item) => {
          if (item.account_name === searchKey) {
            return sum + (item.amount || 0);
          }
          if (item.children) {
            return sum + findAndSumAmount(item.children, searchKey);
          }
          return sum;
        }, 0);
      };

      // Handle the first key
      const [firstKey, ...remainingKeys] = total.keys;
      let totalValue =
        totalsValues[firstKey] ||
        findAndSumAmount(Object.values(data), firstKey);

      // Process remaining keys based on subtract flag
      remainingKeys.forEach((key) => {
        const keyValue =
          totalsValues[key] || findAndSumAmount(Object.values(data), key);
        if (total.subtract) {
          totalValue -= keyValue;
        } else {
          totalValue += keyValue;
        }
      });

      totalsValues[total.name] = totalValue;
    });
  };

  const processReportData = (
    data: any,
    options?: ProcessReportDataOptions,
  ): FormattedReportItem[] => {
    if (!data) return [];

    const defaultOptions: ProcessReportDataOptions = {
      orderedKeys: [],
      parentLevel: 0,
      totals: [],
      expandedKeys: new Set(),
    };
    const {
      orderedKeys,
      parentLevel,
      totals,
      collapsedKeys,
      expandedKeys,
      showTabs = false,
    } = {
      ...defaultOptions,
      ...options,
    };
    const totalsValues: { [key: string]: number } = {};

    calculateTotals(totals, totalsValues, data);

    const flattenData = (
      item: any,
      level: number = 0,
    ): FormattedReportItem[] => {
      const hasChildren = item.children && item.children.length > 0;
      const isCollapsed =
        collapsedKeys?.includes(item.account_name) &&
        !expandedKeys?.has(item.account_name);

      // Create current item row with toggle functionality
      const parentRow: FormattedReportItem = {
        account_code: (
          <LinkText to={buildTransactionLink(item?.id)}>
            <b>{item.account_name}</b>,
          </LinkText>
        ),
      };
      const isParentLevel = level <= (parentLevel || 0);

      const currentRow = isParentLevel
        ? parentRow
        : formatReportItem({
            item,
            boldKeys: hasChildren ? [item.account_name] : [],
            collapsedKeys,
            expandedKeys,
            level: !showTabs ? 1 : level,
          });

      // Initialize result with current row
      const result: FormattedReportItem[] = [currentRow];

      const addTotalsRow = (result: FormattedReportItem[]) =>
        result.push({
          account_code: <b>{`Total ${item.account_name}`}</b>,
          amount: <b>{numberFormat(item.amount)}</b>,
        });

      // Process children only if not collapsed
      if (hasChildren && !isCollapsed) {
        // Order children according to orderedKeys
        const orderedChildren =
          orderedKeys && orderedKeys.length
            ? [
                // include ordered items that exist in children
                ...orderedKeys
                  .map((key) =>
                    item.children.find(
                      (child: any) => child.account_name === key,
                    ),
                  )
                  .filter(Boolean),
                //  include remaining children that aren't in orderedKeys
                ...item.children.filter(
                  (child: any) => !orderedKeys.includes(child.account_name),
                ),
              ]
            : item.children;

        const childrenRows = orderedChildren.flatMap((child: any) =>
          flattenData(child, level + 1),
        );
        result.push(...childrenRows);
        if (isParentLevel) {
          addTotalsRow(result);
        }
      } else {
        if (isParentLevel) {
          addTotalsRow(result);
        }
      }

      return result;
    };

    // Process the main data object
    const mainData = Object.entries(data).flatMap(
      ([, value]: [string, any]) => {
        return flattenData(value);
      },
    );

    const totalRows =
      totals && totals.length
        ? totals?.map((total) => ({
            account_code: <b>{total.name || "Totals"}</b>,
            amount: <b>{numberFormat(totalsValues[total.name])}</b>,
          }))
        : [];

    // If there's an 'after' property, insert the totals after the specified row
    if (totals?.length) {
      let modifiedData = [...mainData];
      const totalsToAppend: FormattedReportItem[] = [];
      totals.forEach((total) => {
        // const totalRow = {
        //   account_code: <b>{total.name || "Totals"}</b>,
        //   amount: <b>{numberFormat(totalsValues[total.name])}</b>,
        // };
        const totalRow = createTotalWithTooltip(
          total,
          totalsValues[total.name],
        );

        if (total.after) {
          const afterIndex = modifiedData.findIndex(
            (item) =>
              item?.account_name === total.after ||
              (isValidElement(item?.account_name) &&
                item?.account_name?.props?.children === total.after) ||
              (isValidElement(item?.account_code) &&
                item?.account_code?.props?.children === total.after),
          );
          if (afterIndex !== -1) {
            modifiedData = [
              ...modifiedData.slice(0, afterIndex + 1),
              totalRow,
              ...modifiedData.slice(afterIndex + 1),
            ];
          }
        } else {
          // Collect totals without 'after' to append at the end
          totalsToAppend.push(totalRow);
        }
      });
      return [...modifiedData, ...totalsToAppend];
    }

    return [...mainData, ...totalRows];
  };

  // Function to insert net profit into equity children
  const insertNetProfitIntoEquity = (data: any, netProfit: number) => {
    if (!data || !data.equity || netProfit === 0) return data;

    // Deep clone the data to avoid mutating the original
    const modifiedData = JSON.parse(JSON.stringify(data));

    // Create net profit entry
    const netProfitEntry = {
      id: "net-profit-calculated",
      account_name: "Net Profit/Loss",
      general_ledger_code: "",
      account_type: "equity",
      parent_account: modifiedData.equity.id,
      deleted: false,
      is_contra_account: false,
      credit_amount_by_date: null,
      debit_amount_by_date: null,
      children: [],
      amount: netProfit,
    };

    // Add net profit to equity children
    if (!modifiedData.equity.children) {
      modifiedData.equity.children = [];
    }
    modifiedData.equity.children.push(netProfitEntry);

    // Update equity total amount to include net profit
    modifiedData.equity.amount = (modifiedData.equity.amount || 0) + netProfit;

    return modifiedData;
  };

  const getFinancialTableData = (data: any, netProfit: number = 0) => {
    if (!data) return [];

    // Insert net profit into equity before processing
    const dataWithNetProfit = insertNetProfitIntoEquity(data, netProfit);

    const current_assetsConfig = {
      orderedKeys: [
        "Cash at Bank and in Hand",
        "Cash In Hand",
        "MOMO - MTN",
        "Airtel Pay",
        "Cash at Bank",
        "Stanbic Bank",
        "Accounts Receivable",
        "Loan Application Fees Receivable",
        "Boda Insurance Fees Receivable",
        "Boda Tracking Fees Receivable",
        "Loans to Others Receivable",
        "UGG654K Accounts Receivable",
        "UGG897L Accounts Receivable",
        "UGG699C Accounts Receivable",
        "UGG831J Accounts Receivable",
        "Inventory",
      ],
      collapsedKeys: ["Loans to Others Receivable"],
      totals: [
        {
          keys: ["Equity", "Liabilities"],
          name: "Total Liabilities and Equity",
        },
      ],
      parentLevel: 0,
      expandedKeys: expandedKeys,
      showTabs: true,
    };

    return processReportData(dataWithNetProfit, current_assetsConfig);
  };

  // Function to calculate net profit from profit and loss data
  const calculateNetProfit = (data: any): number => {
    if (!data) return 0;

    const findAndSumAmount = (items: any[], searchKey: string): number => {
      return items.reduce((sum, item) => {
        if (item.account_name === searchKey) {
          return sum + (item.amount || 0);
        }
        if (item.children) {
          return sum + findAndSumAmount(item.children, searchKey);
        }
        return sum;
      }, 0);
    };

    const allItems = Object.values(data);

    // Calculate components for net profit
    const income = findAndSumAmount(allItems, "Income");
    const costOfGoodsSold = findAndSumAmount(allItems, "Cost of Goods Sold");
    const operatingExpenses = findAndSumAmount(allItems, "Operating Expenses");
    const revaluationLoss = findAndSumAmount(allItems, "Revaluation Loss");

    // Net Profit = Income - Cost of Goods Sold - Operating Expenses - Revaluation Loss
    const netProfit =
      income - costOfGoodsSold - operatingExpenses - revaluationLoss;

    return netProfit;
  };

  const getProfitLossTableData = (data: any) => {
    if (!data) return [];
    return processReportData(data, {
      orderedKeys: [
        "Loan Application Fees Income",
        "Boda Insurance Fees Income",
        "Boda Tracking Fees Income",
        "Income",
        "Interest Income",
      ],
      totals: [
        {
          keys: ["Income", "Cost of Goods Sold"],
          name: "Gross Profit",
          after: "Total Cost of Goods Sold",
          subtract: true,
        },
        {
          keys: ["Gross Profit", "Operating Expenses"],
          name: "Operating Profit",
          after: "Total Operating Expenses",
          subtract: true,
        },
        {
          keys: ["Operating Profit", "Revaluation Loss"],
          name: "Net Profit/Loss",
          subtract: true,
        },
      ],
    });
  };
  const trialBalance = buildTrialBalance(trialBalanceData);

  const trialBalanceTableColumns = () => [
    { id: "account_code", header: "Account Code" },
    { id: "account_name", header: "Account Name" },
    { id: "debit", header: "Debit" },
    { id: "credit", header: "Credit" },
  ];

  const onToggleAccount = (key: string) => {
    const newKeys = new Set(collapsedAccounts);
    if (newKeys.has(key)) {
      newKeys.delete(key);
    } else {
      newKeys.add(key);
    }
    setCollapsedAccounts(newKeys);
  };

  // an account leaves a side blank rather than reading zero on it
  const trialBalanceFigure = (value: number, bold?: boolean) => {
    const figure = value ? numberFormat(value) : "";
    return <Figure>{bold ? <b>{figure}</b> : figure}</Figure>;
  };

  const renderTrialBalanceAccount = (
    account: TTrialBalanceAccount,
    depth = 0,
  ): any[] => {
    const accountKey = String(
      account.id || account.account_code || account.account_name,
    );
    const hasChildren = account.children.length > 0;
    const isOpen = hasChildren && !collapsedAccounts.has(accountKey);
    const emphasise = (value: React.ReactNode) =>
      hasChildren ? <b>{value}</b> : value;

    const row = {
      account_code: (
        <AccountCode
          style={{
            justifyContent: "start",
            marginLeft: `${depth * 20}px`,
          }}
        >
          {hasChildren && (
            <ClickableSpan
              onClick={() => onToggleAccount(accountKey)}
              style={{ marginRight: 5, fontSize: "0.8em" }}
            >
              {isOpen ? "\u25bc" : "\u25b6"}
            </ClickableSpan>
          )}
          {emphasise(account.account_code)}
        </AccountCode>
      ),
      account_name: (
        <LinkText
          to={buildTransactionLink(account.id)}
          style={{
            justifyContent: "start",
            marginLeft: `${depth * 20}px`,
          }}
        >
          {emphasise(account.account_name)}
        </LinkText>
      ),
      debit: trialBalanceFigure(account.debit, hasChildren),
      credit: trialBalanceFigure(account.credit, hasChildren),
    };

    if (!isOpen) return [row];

    return [
      row,
      ...account.children.flatMap((child) =>
        renderTrialBalanceAccount(child, depth + 1),
      ),
    ];
  };

  const getTrialBalanceTableData = () => {
    if (!trialBalance.groups.length) return [];

    const rows = trialBalance.groups.flatMap((group) => [
      { account_code: <b>{group.label.toUpperCase()}</b> },
      ...group.accounts.flatMap((account) =>
        renderTrialBalanceAccount(account),
      ),
      ...returnObject(group.accounts.length > 1, [
        {
          account_code: <b>{`Total ${group.label}`}</b>,
          debit: trialBalanceFigure(group.debit, true),
          credit: trialBalanceFigure(group.credit, true),
        },
      ]),
    ]);

    return [
      ...rows,
      {
        account_code: <b>Total</b>,
        account_name: trialBalance.isBalanced ? (
          ""
        ) : (
          <OutOfBalance>
            {`Out of balance by ${numberFormat(
              Math.abs(trialBalance.difference),
            )}`}
          </OutOfBalance>
        ),
        debit: trialBalanceFigure(trialBalance.totalDebits, true),
        credit: trialBalanceFigure(trialBalance.totalCredits, true),
      },
    ];
  };

  const AccountingTypeFilter = () => (
    <div>
      <div className="capitalize font12 mb-1">Filter by accounting type</div>
      <Select
        options={[
          { label: "Cash", value: "cash" },
          { label: "Accrual", value: "accrual" },
        ]}
        placeholder="Select accounting type"
        allowClear
        value={filters.accounting_type}
        onChange={(value) => {
          setFilters({ ...filters, accounting_type: value });
        }}
        style={{ minWidth: 150, height: 30 }}
      />
    </div>
  );

  const FinancialPositionFilters = () => (
    <div className="flexRow gap20">
      <div>
        <div className="capitalize font12 mb-1">As at date</div>
        <DatePicker
          onChange={(value: any) => {
            setFilters((prev) => ({
              ...prev,
              end_date: value?.format("YYYY-MM-DD") || null,
            }));
          }}
          value={
            filters.end_date ? dayjs(filters.end_date, "YYYY-MM-DD") : null
          }
          style={{ height: 30 }}
        />
      </div>
      <AccountingTypeFilter />
    </div>
  );

  const ExtenalFilters = () => {
    return (
      <div className="flexRow gap20">
        <div>
          <div className="capitalize font12 mb-1">Filter by date</div>
          <RangePicker
            onChange={(value: any) => {
              setFilters((prev) => ({
                ...prev,
                start_date: value?.[0]?.format("YYYY-MM-DD") || null,
                end_date: value?.[1]?.format("YYYY-MM-DD") || null,
              }));
            }}
            value={[
              filters.start_date
                ? dayjs(filters.start_date, "YYYY-MM-DD")
                : null,
              filters.end_date ? dayjs(filters.end_date, "YYYY-MM-DD") : null,
            ]}
            style={{ height: 30 }}
          />
        </div>
        <AccountingTypeFilter />
      </div>
    );
  };
  const tabs: any[] = [
    {
      key: "financial_positions",
      title: "Financial Positions",
      component: (
        <div className="flexColumn gap20">
          <FinancialPositionFilters />
          <Table
            count={financialPositionData?.count || 0}
            tableTitle="Financial Positions"
            tableColumns={financialPositionTableColumns()}
            tableData={
              financialPositionData
                ? getFinancialTableData(
                    financialPositionData,
                    calculateNetProfit(profitLossData),
                  )
                : []
            }
            noEmptyText={false}
            loading={financialPositionLoading}
            tableHeaderItem={
              <ExportButton
                text="Export"
                title="Export Table Data"
                isDownloading={isDownloading}
                onClick={() => handleDownload("financial_position")}
              />
            }
          />
        </div>
      ),
    },
    {
      key: "profit_and_loss",
      title: "Profit and Loss",
      component: (
        <div className="flexColumn gap20">
          <ExtenalFilters />
          <Table
            count={profitLossData?.count || 0}
            tableTitle="Profit and Loss"
            tableColumns={financialPositionTableColumns()}
            tableData={
              profitLossData ? getProfitLossTableData(profitLossData) : []
            }
            noEmptyText={false}
            loading={profitLossLoading}
            tableHeaderItem={
              <div className="flexRow gap10">
                <ExportButton
                  text="Export All"
                  title="Export All Data"
                  isDownloading={isDownloadingAll}
                  onClick={handleDownloadAllProfitAndLoss}
                />
                <ExportButton
                  text="Export"
                  title="Export Table Data"
                  isDownloading={isDownloading}
                  onClick={() => handleDownload("profit_and_loss")}
                />
              </div>
            }
          />
        </div>
      ),
    },
    {
      key: "trial_balance",
      title: "Trial Balance",
      component: (
        <div className="flexColumn gap20">
          <FinancialPositionFilters />
          <Table
            count={trialBalanceData?.count || 0}
            tableTitle="Trial Balance"
            tableColumns={trialBalanceTableColumns()}
            tableData={getTrialBalanceTableData()}
            noEmptyText={false}
            showPagination={false}
            loading={trialBalanceLoading}
            tableHeaderItem={
              <ExportButton
                text="Export"
                title="Export Table Data"
                isDownloading={isDownloading}
                onClick={() => handleDownload("trial_balance")}
              />
            }
          />
        </div>
      ),
    },
    {
      key: "cash_flow",
      title: "Cash Flow Statement",
      component: (
        <div className="flexColumn gap20">
          <ExtenalFilters />
          <Table
            count={cashFlowData?.count || 0}
            tableTitle="Cash Flow Statement"
            tableColumns={financialPositionTableColumns()}
            tableData={cashFlowData ? getTableData(cashFlowData) : []}
            noEmptyText={false}
            loading={cashFlowLoading}
            tableHeaderItem={
              <ExportButton
                text="Export"
                title="Export Table Data"
                isDownloading={isDownloading}
                onClick={() => handleDownload("cash_flow")}
              />
            }
          />
        </div>
      ),
    },
  ];

  return (
    <Wrapper>
      <div className="container">
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/" className="breadcrumb-item">
                  Home
                </Link>
              ),
            },
            {
              title: "Reports",
            },
          ]}
        />
        <GenericTabView tabs={tabs} />
      </div>
    </Wrapper>
  );
};

export default BalanceSheetReports;

const Wrapper = styled.div`
  // padding: 0 20px 20px 20px;
  @media (max-width: 960px) {
  }
`;

export const AccountCode = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  width: 100%;
  font-family: "Courier New", Courier, monospace;
  font-weight: 500;
`;

const Figure = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  font-variant-numeric: tabular-nums;
`;

const OutOfBalance = styled.span`
  color: #c62828;
`;

const ClickableSpan = styled.span`
  cursor: pointer;
  &:hover {
    color: var(--selected-color);
    text-decoration: underline;
  }
`;
